import clsx from "clsx";
import type { ReactNode } from "react";
import { Icon, type IconName } from "../Icon/Icon";
import styles from "./Badge.module.css";

export type BadgeTone = "neutral" | "accent" | "success" | "warning" | "danger" | "info";

export interface BadgeProps {
  children: ReactNode;
  /** success = pass/in stock, warning = near a limit/low stock, danger = fail/over a limit, info = notice. */
  tone?: BadgeTone;
  /** Pair color with an icon or text; color is never the only signal. */
  icon?: IconName;
  /** A dashed outline for "unknown" states. */
  dashed?: boolean;
  className?: string;
}

/** A short status label (job status, stock state, review state). */
export function Badge({ children, tone = "neutral", icon, dashed, className }: BadgeProps) {
  return (
    <span className={clsx(styles.badge, className)} data-tone={tone} data-dashed={dashed || undefined}>
      {icon ? <Icon name={icon} /> : null}
      <span className={styles.text}>{children}</span>
    </span>
  );
}

export type ComplianceResult = "pass" | "fail" | "cannot_evaluate";

const RESULT_LOOK: Record<ComplianceResult, { tone: BadgeTone; icon: IconName; label: string }> = {
  pass: { tone: "success", icon: "check", label: "Pass" },
  fail: { tone: "danger", icon: "x", label: "Fail" },
  cannot_evaluate: { tone: "neutral", icon: "helpCircle", label: "Cannot evaluate" },
};

/**
 * The three-valued compliance result: ✓ Pass, ✕ Fail, ? Cannot evaluate. "Cannot evaluate" is
 * neutral with a dashed border: never success, never blank (project standing decision).
 */
export function ResultBadge({
  result,
  children,
  className,
}: {
  result: ComplianceResult;
  children?: ReactNode;
  className?: string;
}) {
  const look = RESULT_LOOK[result];
  return (
    <Badge tone={look.tone} icon={look.icon} dashed={result === "cannot_evaluate"} className={className}>
      {children ?? look.label}
    </Badge>
  );
}
